import { Button, Tooltip } from '@mantine/core';
import { MessageSquarePlus } from 'lucide-react';

import { COMMUNICATION_UNAVAILABLE_TEXT } from '@/constants/communication.ts';
import { FeatureFlag } from '@/constants/feature-flags.ts';

import { useFeatureFlag } from '@/hooks/use-feature-flag.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { TaskModalMode } from '@/models/TaskModalMode.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import { type EnrichedAppointmentWithBookingStatus, useBookingStore } from '@/stores/bookingStore.ts';

import { getInstanceId, getPvsPatientId } from '@/utils/appointmentUtils.ts';
import { buildBookingCommunicationContext } from '@/utils/communication-utils.ts';
import { openCommunicationModal } from '@/utils/modal/communication-modal-utils.tsx';
import { buildBookingTaskPrefill, withCommunicationTaskFields } from '@/utils/task-utils.ts';
import { openBookingTaskModal } from '@/utils/task-utils.tsx';

interface Props {
    bookedAppointment: EnrichedAppointmentWithBookingStatus;
}

/**
 * Writes a COMMUNICATION activity into the patient's Insuite case folder for the appointment whose card it
 * sits on. Scoped to that one appointment so the calendar, Betriebsstätte and linked task all match the card.
 */
export const BookingCommunicationAction = ({ bookedAppointment }: Props) => {
    const addBookingTask = useBookingStore((state) => state.addBookingTask);
    const currentPatient = useBookingStore((state) => state.current_patient);
    const instanceId = getInstanceId(bookedAppointment.appointment) ?? '';
    const { data: practitionerData } = useEnrichedPractitionerDataForSpecificInstance(instanceId);
    const { value: isCommunicationVisible } = useFeatureFlag(FeatureFlag.IS_COMMUNICATION_ENABLED);

    if (practitionerData?.pvs_provider !== PVSProvider.DOC_CIRRUS || !isCommunicationVisible) return undefined;

    const bookedPatientId = getPvsPatientId(bookedAppointment.appointment);

    // Undefined when the appointment cannot supply the calendar, insurance type or a DC patient id, which
    // the endpoint requires — resolved up front so the button can be disabled instead of failing on submit.
    const context = buildBookingCommunicationContext({
        formData: bookedAppointment.form_data,
        currentPatient: currentPatient,
        patientId: bookedPatientId,
        instanceId: instanceId,
    });

    const handleClick = (): void => {
        if (!context) return;
        const prefill = buildBookingTaskPrefill({
            bookedAppointments: [bookedAppointment],
            currentPatient: currentPatient,
            practitionerData: practitionerData,
            currentForm: undefined,
            instanceId: instanceId,
        });
        openCommunicationModal({
            context: context,
            // Same prefill the task button builds, narrowed to this appointment, plus the communication fields.
            onSavedWithTask: (content) =>
                openBookingTaskModal({
                    mode: TaskModalMode.EDITOR,
                    prefill: withCommunicationTaskFields({ ...prefill, patient_id: context.patientId }, content),
                    onSaved: addBookingTask,
                }),
        });
    };

    return (
        <Tooltip
            label={COMMUNICATION_UNAVAILABLE_TEXT}
            disabled={!!context}
            multiline
            classNames={{ tooltip: 'max-w-md' }}
        >
            {/* span wrapper so the tooltip still shows while the button itself is disabled. Kept a flex
                container so it does not add line-box space around the button and offset it from its siblings. */}
            <span className="flex">
                <Button
                    variant="outline"
                    leftSection={<MessageSquarePlus className="size-4" />}
                    disabled={!context}
                    onClick={handleClick}
                >
                    Kommunikation
                </Button>
            </span>
        </Tooltip>
    );
};
